import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

import { EmpleadoFotoService } from "src/app/_core/providers/empleado-foto.service";
import {
	EmpleadoLike,
	empleadoEmail,
	empleadoInitials,
	empleadoLabel,
	empleadoTeamsUrl,
	empleadoTieneNombre,
} from "src/app/_core/utils/empleado-display";

/**
 * Visor COMÚN de empleado (modo lectura). Avatar (foto de EntraID con fallback a
 * iniciales) + nombre principal + email pequeño/gris. Al hover muestra acciones de
 * enviar correo (mailto) y chat de Teams. Distingue "sin asignar" (vacío) de
 * "asignado sin nombre" (muestra el email + marca). Único sitio para tocar la
 * presentación de una persona en toda la app.
 */
@Component({
	selector: "app-empleado-display",
	standalone: true,
	imports: [CommonModule, MatIconModule, MatTooltipModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: "./empleado-display.component.html",
	styleUrls: ["./empleado-display.component.scss"],
})
export class EmpleadoDisplayComponent implements OnChanges {
	/** Empleado a mostrar (o null => "sin asignar"). */
	@Input() empleado: EmpleadoLike | null = null;
	/** Texto cuando no hay empleado asignado. */
	@Input() emptyText = "Sin asignar";
	/** Tamaño visual. */
	@Input() size: "sm" | "md" = "md";

	label = "";
	email = "";
	initials = "?";
	teamsUrl = "";
	tieneNombre = false;
	fotoUrl: string | null = null;

	constructor(
		private readonly fotoService: EmpleadoFotoService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnChanges(): void {
		const e = this.empleado;
		this.label = empleadoLabel(e);
		this.email = empleadoEmail(e);
		this.initials = empleadoInitials(e);
		this.teamsUrl = empleadoTeamsUrl(e);
		this.tieneNombre = empleadoTieneNombre(e);
		this.loadFoto();
	}

	get mailUrl(): string {
		return this.email ? `mailto:${this.email}` : "";
	}

	/**
	 * Línea principal: el nombre si existe; si no, un placeholder claro ("Sin nombre
	 * registrado") cuando hay email — para que el email quede SIEMPRE en la línea
	 * secundaria gris, igual que en el resto de personas. Sin nombre ni email, cae al
	 * código/#id.
	 */
	get mainLine(): string {
		if (this.tieneNombre) {
			return this.label;
		}
		return this.email ? "Sin nombre registrado" : this.label;
	}

	/** Línea secundaria (gris): siempre el email cuando existe. */
	get subLine(): string {
		return this.email;
	}

	/** Carga la foto de EntraID (servicio compartido, cacheada); si falla, iniciales. */
	private loadFoto(): void {
		this.fotoUrl = null;
		const email = this.email;
		if (!email) {
			return;
		}
		this.fotoService.getFoto(email).subscribe((url) => {
			// Solo aplica si el empleado mostrado sigue siendo el mismo.
			if (this.email === email) {
				this.fotoUrl = url;
				this.cdr.markForCheck();
			}
		});
	}
}
